import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Modal from "react-modal";
import { getCardTitle } from "../MemberReportMocks.js";
import { convertTo12HourFormat } from "../../../common-data/Converts.js";
import ModalHeadAndClose from "../../../payouts-wtf/common/ModalHeadAndClose.jsx";
import { fetchSegmentMembersData } from "../../../../redux/prospect-reducers/prospectSlice.js";
import CommonTable from "../../../common-components/common-tables/CommonTable.jsx";
import {
  CallIcon,
  CheckCircleIcon,
  ClockIconV3,
  PersonIcon,
  XCircleIcon,
  XIcon,
} from "../../../../images/icons/icons.js";
import DetailModHeader from "../../../common-components/DetailModHeader.jsx";
import TrialAssignForm from "../../../pt/three-day-trial/TrialAssignForm.jsx";
import ThreedayTrialHistory from "../../../pt/three-day-trial/ThreedayTrialHistory.jsx";
import TrainerMemberMap from "../../../trainer-member-engagement/trainer-member-map/TrainerMemberMap.jsx";
import BCAReport from "../../../trainer-member-engagement/bca-report/BCAReport.jsx";

const MemberDetailModal = ({
  userInfoData,
  onClose,
  filterType,
  prevFilters,
  setModValues: setParentModValues,
}) => {
  const dispatch = useDispatch();
  const { segmentMembers, segmentMembersLoading, segmentMembersError } =
    useSelector((state) => state.prospects);

  const [pageLimit, setPageLimit] = useState(10);
  const [isPage, setIsPage] = useState(0);

  // Map frontend filter types to API filter types
  const getApiFilterType = (filterType) => {
    const filterTypeMap = {
      active_trials: "pt_trial",
      unassigned: "unassigned",
      pt_trial: "pt_trial",
      bca_pending: "bca_pending",
      with_trainer: "with_trainer",
      total_checkins: "total_checkins",
    };
    return filterTypeMap[filterType] || filterType;
  };

  const iniFilters = {
    ...prevFilters,
    filter_type: getApiFilterType(filterType),
    limit: pageLimit || 10,
    page: isPage + 1,
  };
  const [filters, setFilters] = useState(iniFilters);

  const [allData, setAllData] = useState({
    data: [],
    pagination: [],
  });

  const iniModValues = {
    isModOpen: false,
    modName: "",
    rowData: "",
  };
  const [modValues, setModValues] = useState(iniModValues);

  const onCloseNestedModal = () => {
    setModValues(iniModValues);
  };

  const getSegmentMembers = (filters) => {
    dispatch(
      fetchSegmentMembersData({
        filters: filters,
        token: userInfoData?.token,
      })
    );
  };

  useEffect(() => {
    getSegmentMembers(filters);
  }, [filters]);

  useEffect(() => {
    setFilters((prevs) => ({
      ...prevs,
      ...prevFilters,
      limit: pageLimit || 10,
      page: isPage + 1,
    }));
  }, [pageLimit, isPage, prevFilters]);

  useEffect(() => {
    if (
      !segmentMembersLoading &&
      !segmentMembersError &&
      segmentMembers?.status
    ) {
      setAllData({
        data: segmentMembers?.data || [],
        pagination: segmentMembers?.pagination || [],
      });
    } else {
      setAllData({
        data: [],
        pagination: [],
      });
    }
  }, [segmentMembersLoading, segmentMembersError, segmentMembers]);

  const getMemberType = (filterType) => {
    switch (filterType) {
      case "unassigned":
        return "unassigned";
      case "active_trials":
      case "pt_trial":
        return "trial";
      case "bca_pending":
        return "bca_pending";
      case "with_trainer":
        return "with_trainer";
      case "total_checkins":
        return "total_checkins";
      default:
        return "default";
    }
  };

  const memberType = getMemberType(filterType);

  const tbColumns = [
    {
      dataField: "user_name",
      text: "Member Info",
      formatter: (_, row) => {
        return (
          <div className="tb-flex-col">
            <div className="tb-flex">
              <span className="tb-data tb-info-icon">
                <PersonIcon />
              </span>
              <span className="tb-data">{row?.user_name || "NA"}</span>
            </div>

            <div className="tb-flex">
              <span className="tb-data tb-info-icon">
                <CallIcon />
              </span>
              <span className="tb-data">{row?.user_mobile || "NA"}</span>
            </div>
          </div>
        );
      },
    },
    {
      dataField: "gym_name",
      text: "Gym Name",
      formatter: (_, row) => {
        const gymName = row?.gym_name || "NA";
        const isLong = gymName.length > 30;

        return (
          <span className={`tb-data ${isLong ? "tb-data-long" : ""}`}>
            {gymName}
          </span>
        );
      },
    },
    {
      dataField: "member_checkin_time",
      text: "Check-in Time",
      formatter: (_, row) => {
        return (
          <div className="tb-flex">
            {row.member_checkin_time ? (
              <>
                <span className="tb-data tb-info-icon">
                  <ClockIconV3 color="#2c3e50" />
                </span>
                <span className="tb-data">
                  {convertTo12HourFormat(row.member_checkin_time)}
                </span>
              </>
            ) : (
              <span className="tb-data">-</span>
            )}
          </div>
        );
      },
    },
    {
      dataField: "trainer_name",
      text: "Assigned Trainer",
      formatter: (_, row) => {
        return (
          <div className="tb-flex-col">
            {row?.trainer_name ? (
              <>
                <div className="tb-flex">
                  <span className="tb-data tb-info-icon">
                    <PersonIcon />
                  </span>
                  <span className="tb-data">{row?.trainer_name || "NA"}</span>
                </div>

                <div className="tb-flex">
                  <span className="tb-data tb-info-icon">
                    <CallIcon />
                  </span>
                  <span className="tb-data">{row?.trainer_mobile || "NA"}</span>
                </div>
              </>
            ) : (
              <span className="tb-badge not-assigned">
                <XIcon color="#fff" /> not assigned
              </span>
            )}
          </div>
        );
      },
    },
    {
      dataField: "trial_days",
      text: "Trial Status",
      formatter: (_, row) => {
        return (
          <div className="tb-flex-col">
            {row?.not_started ? (
              <span className="tb-badge not-assigned">trial not started</span>
            ) : row.trial_days !== undefined && row.trial_days !== null ? (
              <span
                className={`tb-badge ${
                  parseInt(row.trial_days) === 1
                    ? "tb-badge-success-1"
                    : parseInt(row.trial_days) === 2
                    ? "tb-badge-success-2"
                    : parseInt(row.trial_days) === 3
                    ? "tb-badge-success"
                    : ""
                }`}
              >
                Trial Day {parseInt(row.trial_days) || 0}
              </span>
            ) : (
              <span className="tb-data">-</span>
            )}

            {row?.three_day_assigned === 1 && (
              <div className="tb-flex" style={{ marginTop: "4px" }}>
                <span className="tb-data tb-icon">
                  {row?.three_day_assigned ? (
                    <CheckCircleIcon />
                  ) : (
                    <XCircleIcon />
                  )}
                </span>
                <span className="tb-data">
                  Trial: {row?.three_day_done_count || 0}/3
                </span>
              </div>
            )}
          </div>
        );
      },
    },
    {
      dataField: "bca_status",
      text: "BCA Status",
      formatter: (_, row) => {
        return (
          <div className="tb-flex-col">
            <span
              className={`tb-data tb-badge ${
                row?.bca_status === "completed"
                  ? "tb-badge-success"
                  : row?.bca_status === "pending"
                  ? "tb-badge-success-2"
                  : "not-assigned"
              }`}
            >
              {row?.bca_status?.replace(/_/g, " ") || "NA"}
            </span>
          </div>
        );
      },
    },
    {
      dataField: "profiling_status",
      text: "Profiling status",
      formatter: (_, row) => {
        return (
          <div className="tb-flex-col">
            <div className="tb-flex">
              <span
                className={`tb-data tb-badge ${
                  row?.profiling_status === "done"
                    ? "tb-badge-success"
                    : row?.profiling_status === "pending"
                    ? "tb-badge-success-2"
                    : "not-assigned"
                }`}
                style={{ alignItems: "center" }}
              >
                {row?.profiling_status?.replace(/_/g, " ") || "NA"}
              </span>
              {row?.profiling_status === "done" && (
                <span className="tb-data">Date: {row?.profiling_date}</span>
              )}
            </div>

            {row?.profiling_status === "done" && (
              <div className="tb-flex">
                <span className="tb-data">
                  Weight: {Number(row?.profiling_weight || 0).toFixed(2)} kg
                </span>{" "}
                <span className="tb-data">
                  BMI:{" "}
                  {row?.profiling_bmi
                    ? Number(row.profiling_bmi).toFixed(2)
                    : "-"}
                </span>
              </div>
            )}
          </div>
        );
      },
    },
    {
      dataField: "actions",
      text: "Actions",
      formatter: (_, row) => {
        return (
          <div className="pp-employee-payroll-actions">
            {memberType === "unassigned" && (
              <button
                className="pp-employee-payroll-action-button pp-outlined-border"
                onClick={() => {
                  setModValues({
                    isModOpen: true,
                    modName: "trainer_member_map",
                    rowData: row,
                  });
                }}
              >
                Map Trainer
              </button>
            )}

            {memberType === "trial" && (
              <button
                className="pp-employee-payroll-action-button pp-outlined-border"
                onClick={() => {
                  setModValues({
                    isModOpen: true,
                    modName: "trial_assign",
                    rowData: row,
                  });
                }}
              >
                {row?.not_started ? "Assign" : "Re-Assign"} Trial
              </button>
            )}

            {memberType === "bca_pending" && (
              <button
                className="pp-employee-payroll-action-button pp-outlined-border"
                onClick={() => {
                  setModValues({
                    isModOpen: true,
                    modName: "BCA_history",
                    rowData: row,
                  });
                }}
              >
                View BCA
              </button>
            )}

            {(memberType === "with_trainer" ||
              memberType === "total_checkins") && (
              <button
                className="pp-employee-payroll-action-button pp-outlined-border"
                onClick={() => {
                  setModValues({
                    isModOpen: true,
                    modName: "trial_history",
                    rowData: row,
                  });
                }}
              >
                View Details
              </button>
            )}
          </div>
        );
      },
    },
  ].filter(Boolean);

  return (
    <>
      <Modal
        isOpen={modValues.isModOpen}
        onRequestClose={onCloseNestedModal}
        contentLabel="Detail Modal"
        ariaHideApp={false}
      >
        {modValues.modName === "trial_assign" ? (
          <div className="createNewCTOModalContainer mod-width-50p mod-height-60vh">
            <TrialAssignForm
              rowData={modValues.rowData}
              closeModal={onCloseNestedModal}
              userInfoData={userInfoData}
              callFunction={() => {
                onCloseNestedModal();
                getSegmentMembers(filters);
              }}
            />
          </div>
        ) : modValues.modName === "trial_history" ? (
          <div className="createNewCTOModalContainer mod-width-95vw mod-height-95vh">
            <DetailModHeader
              info={modValues.rowData}
              onClose={onCloseNestedModal}
              isOnlySubscription={true}
            />
            <ThreedayTrialHistory
              rowData={modValues.rowData}
              userInfoData={userInfoData}
            />
          </div>
        ) : modValues.modName === "trainer_member_map" ? (
          <div className="createNewCTOModalContainer mod-width-50p">
            <DetailModHeader
              info={modValues.rowData}
              onClose={onCloseNestedModal}
              isOnlySubscription={true}
            />
            <TrainerMemberMap
              userInfoData={userInfoData}
              rowData={modValues.rowData}
              closeModal={onCloseNestedModal}
              callFunction={() => {
                onCloseNestedModal();
                getSegmentMembers(filters);
              }}
            />
          </div>
        ) : modValues.modName === "BCA_history" ? (
          <div className="createNewCTOModalContainer mod-width-95vw mod-height-95vh">
            <DetailModHeader
              info={modValues.rowData}
              onClose={onCloseNestedModal}
              isOnlySubscription={true}
            />
            <BCAReport
              userInfoData={userInfoData}
              rowData={modValues.rowData}
            />
          </div>
        ) : null}
      </Modal>

      <div className="createNewCTOModalContainer mod-width-95vw mod-height-90vh">
        <ModalHeadAndClose
          title={getCardTitle(filterType)}
          onClose={onClose}
          isReload={() => {
            getSegmentMembers(filters);
          }}
        />

        <div className="common-table-container mt15">
          <CommonTable
            tableData={allData.data}
            tablePagination={allData.pagination}
            tableColumns={tbColumns}
            isPage={isPage}
            setIsPage={setIsPage}
            pageLimit={pageLimit}
            setPageLimit={setPageLimit}
            isLoading={segmentMembersLoading}
          />
        </div>
      </div>
    </>
  );
};

export default MemberDetailModal;

